/**
 * Entity Resolution Utilities
 * 
 * Shared utilities for resolving entity IDs to display names/titles
 * and orchestrated data loading for modals.
 * 
 * Used by: UnifiedActionReview, MergeConfirmationModal, SplitFeatureModal
 */

import { 
  collectionActionsApi, 
  goalsApi, 
  personasApi, 
  epicsApi, 
  initiativesApi, 
  releasesApi, 
  teamApi, 
  productTagsApi,
  productsApi
} from '../../../services/api';

/**
 * Resolve an entity ID to its display title/name
 * 
 * @param {string} entityId - The entity ID to resolve
 * @param {string} refEntityType - The type of entity (epic, persona, user, tag, etc.)
 * @param {Object} linkableEntities - Map of entity type -> array of entities
 * @returns {string} The resolved title/name or the original ID if not found
 */
export const resolveEntityTitle = (entityId, refEntityType, linkableEntities) => {
  if (!entityId || !refEntityType || !linkableEntities) return entityId;
  
  const entities = linkableEntities[refEntityType] || [];
  
  // Determine the ID field based on entity type
  const idField = `${refEntityType}_id`;
  
  // Special case for tags which use tag_id
  const entity = entities.find(e => {
    // Try multiple ID field patterns
    return e[idField] === entityId || 
           e.id === entityId || 
           e._id === entityId ||
           e.tag_id === entityId ||  // For product tags
           e.user_id === entityId;   // For users
  });
  
  if (entity) {
    return entity.title || entity.name || entity.email || entity.metric_name || entityId;
  }
  
  return entityId;
};

/**
 * Resolve an array of entity IDs to their display titles
 * 
 * @param {Array<string>} entityIds - Array of entity IDs
 * @param {string} refEntityType - The type of entity
 * @param {Object} linkableEntities - Map of entity type -> array of entities
 * @returns {Array<string>} Array of resolved titles
 */
export const resolveEntityTitles = (entityIds, refEntityType, linkableEntities) => {
  if (!Array.isArray(entityIds)) return [];
  return entityIds.map(id => resolveEntityTitle(id, refEntityType, linkableEntities));
};

/**
 * Get the ID field name for an entity based on its ref type
 * 
 * @param {string} refEntityType - The entity type
 * @returns {string} The ID field name
 */
export const getEntityIdField = (refEntityType) => {
  const idFieldMap = {
    user: 'user_id',
    tag: 'tag_id',
    epic: 'epic_id',
    persona: 'persona_id',
    goal: 'goal_id',
    initiative: 'initiative_id',
    release: 'release_id',
    feature: 'feature_id',
    segment: 'segment_id',
    // KPIs use composite keys (goal_id:kpi_id) stored in kpi_key field
    kpi: 'kpi_key',
  };
  return idFieldMap[refEntityType] || `${refEntityType}_id`;
};

/**
 * Fetch all linkable entities for the given ref types
 * 
 * @param {Set<string>|Array<string>} refEntityTypes - Set or array of ref entity types to fetch
 * @param {string} productId - The product ID for product-scoped entities
 * @returns {Promise<Object>} Map of entity type -> array of entities
 */
export const fetchLinkableEntities = async (refEntityTypes, productId) => {
  const linkables = {};
  const typesArray = Array.isArray(refEntityTypes) ? refEntityTypes : Array.from(refEntityTypes);
  
  if (typesArray.length === 0) return linkables;
  
  const fetchPromises = typesArray.map(async (refType) => {
    try {
      if (refType === 'user') {
        const response = await teamApi.list();
        // Team API returns { members: [...] }
        if (response?.data?.members && Array.isArray(response.data.members)) {
          return { refType, data: response.data.members };
        }
      } else if (productId) {
        if (refType === 'goal') {
          const response = await goalsApi.list(productId);
          if (response?.data && Array.isArray(response.data)) {
            return { refType, data: response.data };
          }
        } else if (refType === 'persona') {
          const response = await personasApi.list(productId);
          if (response?.data && Array.isArray(response.data)) {
            return { refType, data: response.data };
          }
        } else if (refType === 'epic') {
          const response = await epicsApi.listByProduct(productId);
          if (response?.data && Array.isArray(response.data)) {
            return { refType, data: response.data };
          }
        } else if (refType === 'initiative') {
          const response = await initiativesApi.listByProduct(productId);
          if (response?.data && Array.isArray(response.data)) {
            return { refType, data: response.data };
          }
        } else if (refType === 'release') {
          const response = await releasesApi.list(productId);
          if (response?.data && Array.isArray(response.data)) {
            return { refType, data: response.data };
          }
        } else if (refType === 'tag') {
          const tagResponse = await productTagsApi.list(productId);
          if (tagResponse?.data?.tags && Array.isArray(tagResponse.data.tags)) {
            return { refType, data: tagResponse.data.tags };
          }
        } else if (refType === 'kpi') {
          // KPIs are embedded in goals
          const goalsResponse = await goalsApi.list(productId);
          if (goalsResponse?.data && Array.isArray(goalsResponse.data)) {
            const allKpis = [];
            goalsResponse.data.forEach(goal => {
              (goal.kpis || []).forEach(kpi => {
                allKpis.push({
                  kpi_key: `${goal.goal_id}:${kpi.kpi_id}`,
                  kpi_id: kpi.kpi_id,
                  goal_id: goal.goal_id,
                  goal_title: goal.title,
                  metric_name: kpi.metric_name,
                  title: kpi.metric_name
                });
              });
            });
            return { refType, data: allKpis };
          }
        } else if (refType === 'segment') {
          // Segments are embedded in product.market_segments
          const productResponse = await productsApi.get(productId);
          if (productResponse?.data?.market_segments && Array.isArray(productResponse.data.market_segments)) {
            return { 
              refType, 
              data: productResponse.data.market_segments.map(s => ({
                segment_id: s.segment_id,
                title: s.title,
                description: s.description
              }))
            };
          }
        }
      }
    } catch (e) {
      console.warn(`[entityResolution] Could not fetch linkable ${refType}:`, e);
    }
    return null;
  });
  
  const results = await Promise.all(fetchPromises);
  results.forEach(result => {
    if (result) {
      linkables[result.refType] = result.data;
    }
  });
  
  return linkables;
};

/**
 * Extract ref entity types from a field schema
 * 
 * @param {Object} fieldSchema - The field schema object
 * @returns {Set<string>} Set of ref entity types found in the schema
 */
export const extractRefEntityTypes = (fieldSchema) => {
  const refTypes = new Set();
  
  if (!fieldSchema || typeof fieldSchema !== 'object') return refTypes;
  
  for (const fieldConfig of Object.values(fieldSchema)) {
    if (fieldConfig?.ref_entity) {
      refTypes.add(fieldConfig.ref_entity);
    }
  }
  
  return refTypes;
};

/**
 * Check if a field is a reference field (single or array)
 * 
 * @param {Object} fieldConfig - The field configuration from schema
 * @returns {boolean}
 */
export const isRefField = (fieldConfig) => {
  return fieldConfig?.ref_entity != null;
};

/**
 * Check if a field is a single reference field
 * 
 * @param {Object} fieldConfig - The field configuration from schema
 * @returns {boolean}
 */
export const isSingleRefField = (fieldConfig) => {
  return fieldConfig?.ref_entity != null && fieldConfig?.type !== 'array';
};

/**
 * Check if a field is a multi-reference (array) field
 * 
 * @param {Object} fieldConfig - The field configuration from schema
 * @returns {boolean}
 */
export const isMultiRefField = (fieldConfig) => {
  return fieldConfig?.ref_entity != null && fieldConfig?.type === 'array';
};

/**
 * Get options for a ref field dropdown
 * 
 * For KPIs, returns options with breadcrumb-style labels: "Goal Name / KPI Name"
 * The value is the composite key (goal_id:kpi_id) for proper data binding.
 * 
 * @param {string} refEntityType - The entity type
 * @param {Object} linkableEntities - Map of entity type -> array of entities
 * @returns {Array<{value: string, label: string, ...}>} Options for select/multi-select
 */
export const getRefFieldOptions = (refEntityType, linkableEntities) => {
  const entities = linkableEntities[refEntityType] || [];
  const idField = getEntityIdField(refEntityType);
  
  // Special handling for KPIs - use breadcrumb format with goal context
  if (refEntityType === 'kpi') {
    return entities.map(entity => ({
      value: entity.kpi_key,  // Composite key: goal_id:kpi_id
      label: `${entity.goal_title || 'Unknown Goal'} / ${entity.metric_name || 'Unnamed KPI'}`,
      // Additional context for grouping/display
      goalId: entity.goal_id,
      goalTitle: entity.goal_title,
      kpiId: entity.kpi_id,
      metricName: entity.metric_name,
      source: entity.source,
      sourceName: entity.source_name,
    }));
  }
  
  // Standard handling for other entity types
  return entities.map(entity => ({
    value: entity[idField] || entity.id || entity._id,
    label: entity.title || entity.name || entity.email || entity.metric_name || entity[idField] || 'Unknown',
  }));
};

// =============================================================================
// SHARED ENTITY CONFIGURATION
// =============================================================================

/**
 * Field type constants for forms
 */
export const STATUS_OPTIONS = ['draft', 'active', 'in_progress', 'completed', 'archived'];
export const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
export const REQUEST_TYPE_OPTIONS = ['feature', 'bug', 'enhancement', 'support'];
export const REQUEST_STAGE_OPTIONS = ['new', 'under_review', 'accepted', 'rejected', 'committed'];

/**
 * Fields that should render as textarea (long text)
 */
export const TEXTAREA_FIELDS = new Set([
  'description',
  'user_story',
  'requirements',
  'hypothesis',
  'success_criteria',
  'problem_statement',
  'goals',
  'pain_points',
]);

/**
 * Entity configuration for display and editing
 * Used by: UnifiedActionReview, MergeConfirmationModal, SplitFeatureModal
 */
export const ENTITY_CONFIG = {
  goal: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'status', 'target_date', 'owner_id'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      status: 'Status',
      target_date: 'Target Date',
      owner_id: 'Owner',
    },
    editableFields: ['title', 'description', 'status', 'target_date', 'owner_id'],
    childEntityType: 'kpi',
    childRelationLabel: 'KPIs',
  },
  kpi: {
    primaryField: 'metric_name',
    displayFields: ['metric_name', 'description', 'metric_format', 'direction', 'start_value', 'target_value', 'measurement_interval'],
    fieldLabels: {
      metric_name: 'Metric Name',
      description: 'Description',
      metric_format: 'Format',
      direction: 'Direction',
      start_value: 'Start Value',
      target_value: 'Target Value',
      measurement_interval: 'Interval',
    },
    editableFields: ['metric_name', 'description'],
    childEntityType: null,
    childRelationLabel: null,
  },
  initiative: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'hypothesis', 'success_criteria', 'target_quarter', 'status', 'owner_id'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      hypothesis: 'Hypothesis',
      success_criteria: 'Success Criteria',
      target_quarter: 'Target Quarter',
      status: 'Status',
      owner_id: 'Owner',
    },
    editableFields: ['title', 'description', 'hypothesis', 'success_criteria', 'target_quarter', 'status', 'owner_id'],
    childEntityType: 'epic',
    childRelationLabel: 'Epics',
  },
  epic: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'problem_statement', 'target_quarter', 'status', 'initiative_id', 'persona_ids', 'owner_id'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      problem_statement: 'Problem Statement',
      target_quarter: 'Target Quarter',
      status: 'Status',
      initiative_id: 'Initiative',
      persona_ids: 'Personas',
      owner_id: 'Owner',
    },
    editableFields: ['title', 'description', 'problem_statement', 'target_quarter', 'status', 'initiative_id', 'persona_ids', 'owner_id'],
    childEntityType: 'feature',
    childRelationLabel: 'Features',
  },
  feature: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'user_story', 'requirements', 'priority_level', 'status', 'epic_id', 'persona_ids', 'tag_ids', 'assignee_id'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      user_story: 'User Story',
      requirements: 'Acceptance Criteria',
      priority_level: 'Priority',
      status: 'Status',
      epic_id: 'Epic',
      persona_ids: 'Personas',
      tag_ids: 'Tags',
      assignee_id: 'Assignee',
    },
    editableFields: ['title', 'description', 'user_story', 'priority_level', 'status', 'epic_id', 'persona_ids', 'tag_ids', 'assignee_id'],
    childEntityType: null,
    childRelationLabel: null,
  },
  persona: {
    primaryField: 'name',
    displayFields: ['name', 'role', 'description', 'goals', 'pain_points'],
    fieldLabels: {
      name: 'Name',
      role: 'Role',
      description: 'Description',
      goals: 'Goals',
      pain_points: 'Pain Points',
    },
    editableFields: ['name', 'role', 'description', 'goals', 'pain_points'],
    childEntityType: null,
    childRelationLabel: null,
  },
  release: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'release_date', 'status'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      release_date: 'Release Date',
      status: 'Status',
    },
    editableFields: ['title', 'description', 'release_date', 'status'],
    childEntityType: null,
    childRelationLabel: null,
  },
  request: {
    primaryField: 'title',
    displayFields: ['title', 'description', 'type', 'stage', 'priority_level'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
      type: 'Request Type',
      stage: 'Stage',
      priority_level: 'Priority',
    },
    editableFields: ['title', 'description', 'type', 'stage', 'priority_level'],
    childEntityType: null,
    childRelationLabel: null,
  },
  product: {
    primaryField: 'name',
    displayFields: [
      'competitive_positioning', 'market_size_tam', 'market_size_sam', 'market_size_som',
      'market_segments', 'key_competitors'
    ],
    fieldLabels: {
      name: 'Product Name',
      competitive_positioning: 'Competitive Positioning',
      market_size_tam: 'TAM (Total Addressable Market)',
      market_size_sam: 'SAM (Serviceable Addressable Market)',
      market_size_som: 'SOM (Serviceable Obtainable Market)',
      market_segments: 'Market Segments',
      key_competitors: 'Key Competitors',
    },
    editableFields: ['name', 'competitive_positioning'],
    childEntityType: null,
    childRelationLabel: null,
  },
  // Embedded entities
  market_segment: {
    primaryField: 'title',
    displayFields: ['title', 'description'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
    },
    editableFields: ['title', 'description'],
    childEntityType: null,
    childRelationLabel: null,
  },
  // Segment (alias for market_segment - used by segment_id ref field)
  segment: {
    primaryField: 'title',
    displayFields: ['title', 'description'],
    fieldLabels: {
      title: 'Title',
      description: 'Description',
    },
    editableFields: ['title', 'description'],
    childEntityType: null,
    childRelationLabel: null,
  },
  competitor: {
    primaryField: 'name',
    displayFields: ['name', 'description', 'strengths', 'weaknesses', 'url'],
    fieldLabels: {
      name: 'Competitor Name',
      description: 'Description',
      strengths: 'Strengths',
      weaknesses: 'Weaknesses',
      url: 'Website URL',
    },
    editableFields: ['name', 'description', 'strengths', 'weaknesses'],
    childEntityType: null,
    childRelationLabel: null,
  },
  requirement: {
    primaryField: 'text',
    displayFields: ['text', 'is_completed'],
    fieldLabels: {
      text: 'Requirement Text',
      is_completed: 'Completed',
    },
    editableFields: ['text', 'is_completed'],
    childEntityType: null,
    childRelationLabel: null,
  },
  kpi_alignment: {
    primaryField: 'hypothesis',
    displayFields: ['kpi_key', 'hypothesis'],
    fieldLabels: {
      kpi_key: 'KPI',
      hypothesis: 'Hypothesis',
    },
    editableFields: ['kpi_key', 'hypothesis'],
    childEntityType: null,
    childRelationLabel: null,
  },
  metric_alignment: {
    primaryField: 'hypothesis',
    displayFields: ['goal_id', 'kpi_id', 'hypothesis'],
    fieldLabels: {
      goal_id: 'Goal',
      kpi_id: 'KPI',
      hypothesis: 'Hypothesis',
    },
    editableFields: ['hypothesis'],
    childEntityType: null,
    childRelationLabel: null,
  },
  feature_link: {
    primaryField: 'title',
    displayFields: ['title', 'url'],
    fieldLabels: {
      title: 'Link Title',
      url: 'URL',
    },
    editableFields: ['title', 'url'],
    childEntityType: null,
    childRelationLabel: null,
  },
};

/**
 * Get entity config with fallback defaults
 */
export const getEntityConfig = (entityType) => {
  return ENTITY_CONFIG[entityType] || {
    primaryField: 'title',
    displayFields: ['title', 'description'],
    fieldLabels: { title: 'Title', description: 'Description' },
    editableFields: ['title', 'description'],
  };
};

